import { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Card, Modal, Typography } from "antd";
import { FileOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";
import VideoPlayer from "../../../Components/VideoPlayer";
import { apiFetch } from "../../../services/apiClient";
import { downloadFilesAsZip } from "../../../utils/downloadHelpers";
import { buildFlujoDownloadUrl, type FlujoFile } from "../../flujos/flujos.api";
import { ImagenVista, Miniatura, Pdf } from "../../flujos/components/AdjuntosEnFila";

export interface GrupoAdjuntosLavanderia {
  operacionId: number;
  movimiento: { id: number; tipo: string; fecha: string; descripcion: string; monto: number };
  archivos: FlujoFile[];
}

/** Una consulta por período o por página, nunca una petición por cada fila. */
export function useAdjuntosLavanderia(lavanderiaId: number | null, filtro: { periodo?: string; lavados?: number[] }) {
  const [grupos, setGrupos] = useState<GrupoAdjuntosLavanderia[]>([]);
  const secuencia = useRef(0);
  const ids = (filtro.lavados || []).join(",");
  const periodo = filtro.periodo || "";
  const cargar = useCallback(async () => {
    const version = ++secuencia.current;
    if (!lavanderiaId || (!periodo && !ids)) { setGrupos([]); return; }
    const partes = periodo ? [`periodo=${encodeURIComponent(periodo)}`] : Array.from({ length: Math.ceil(ids.split(",").length / 100) }, (_, i) => `lavados=${ids.split(",").slice(i * 100, i * 100 + 100).join(",")}`);
    const respuestas = await Promise.all(partes.map((q) => apiFetch<{ data: GrupoAdjuntosLavanderia[] }>(`/finanzas/empresa/lavanderias/${lavanderiaId}/adjuntos-bot?${q}`)));
    if (version === secuencia.current) setGrupos(respuestas.flatMap((r) => r?.data || []));
  }, [lavanderiaId, periodo, ids]);
  useEffect(() => { setGrupos([]); void cargar(); return () => { secuencia.current++; }; }, [cargar]);
  return { grupos, cargar };
}

export function AdjuntosLavanderia({ grupos, actualizar }: { grupos: GrupoAdjuntosLavanderia[]; actualizar: () => Promise<void> }) {
  const [activo, setActivo] = useState<number | null>(null);
  const [indice, setIndice] = useState(0);
  const [zoom, setZoom] = useState(1);
  const inicioToque = useRef<number | null>(null);
  const grupo = grupos.find((g) => g.operacionId === activo);
  const archivos = grupo?.archivos || [];
  const archivo = archivos[indice];
  useEffect(() => { setZoom(1); }, [indice, archivo?.key]);
  useEffect(() => {
    if (activo === null) return;
    const navegar = (e: KeyboardEvent) => {
      const destino = e.target;
      if (destino instanceof Element && destino.closest("input, textarea, select, video, [contenteditable=true]")) return;
      if (e.key === "ArrowLeft") setIndice((i) => Math.max(0, i - 1));
      if (e.key === "ArrowRight") setIndice((i) => Math.max(0, Math.min(archivos.length - 1, i + 1)));
    };
    document.addEventListener("keydown", navegar);
    return () => document.removeEventListener("keydown", navegar);
  }, [activo, archivos.length]);
  const mover = (paso: number) => { setIndice((i) => Math.max(0, Math.min(archivos.length - 1, i + paso))); setZoom(1); };
  if (!grupos.length) return null;
  return <>
    <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
      {grupos.map((g) => <button type="button" className="flujo-adjunto-boton" key={g.operacionId}
        aria-label={`Ver ${g.archivos.length} archivo(s) de ${g.movimiento.descripcion}`} title={`${g.movimiento.descripcion} · ${g.archivos.length} archivo(s)`}
        onClick={() => { setActivo(g.operacionId); setIndice(0); setZoom(1); void actualizar(); }}>
        <Miniatura file={g.archivos[0]} />
      </button>)}
    </div>
    <Modal open={activo !== null} title={grupo?.movimiento.descripcion || "Archivos de lavandería"} footer={null} width={950} onCancel={() => setActivo(null)} destroyOnClose>
      {archivo ? <>
        <div className="flujo-adjunto-toolbar"><span>{grupo?.movimiento.fecha} · {indice + 1} de {archivos.length} · {archivo.originalName}</span>
          {archivos.length > 1 && <Button onClick={() => void downloadFilesAsZip(archivos.map((f) => ({ url: buildFlujoDownloadUrl(f), name: f.originalName })), `lavanderia-${activo}-adjuntos`)}>Descargar todos</Button>}
        </div>
        <div className="flujo-adjunto-visor" onTouchStart={(e) => { inicioToque.current = e.touches[0]?.clientX ?? null; }} onTouchEnd={(e) => { if (inicioToque.current == null || zoom > 1) return; const delta = e.changedTouches[0].clientX - inicioToque.current; if (Math.abs(delta) > 60) mover(delta < 0 ? 1 : -1); inicioToque.current = null; }}>
          <Button aria-label="Archivo anterior" disabled={indice <= 0} icon={<LeftOutlined />} onClick={() => mover(-1)} />
          <div className="flujo-adjunto-contenido" key={archivo.key}>
            {archivo.kind === "image" ? <ImagenVista file={archivo} onZoomChange={setZoom} onSwipe={mover} /> : archivo.kind === "video" ? <VideoPlayer src={archivo.url} previewSrc={archivo.previewUrl} nombreArchivo={archivo.originalName} altoMaximo="60dvh" playsInline /> : archivo.mimeType === "application/pdf" || /\.pdf$/i.test(archivo.originalName) ? <Pdf file={archivo} /> : <div className="flujo-adjunto-documento"><FileOutlined /><p>{archivo.originalName}</p><p>Descarga el archivo para abrirlo.</p></div>}
          </div>
          <Button aria-label="Archivo siguiente" disabled={indice >= archivos.length - 1} icon={<RightOutlined />} onClick={() => mover(1)} />
        </div>
        <div className="flujo-adjunto-pie"><div className="flujo-adjunto-tira">{archivos.map((f, i) => <button key={f.key} type="button" onClick={() => { setIndice(i); setZoom(1); }} className={`flujo-adjunto-boton ${i === indice ? "activo" : ""}`} aria-label={`Ver ${f.originalName}`}><Miniatura file={f} /></button>)}</div><Button href={buildFlujoDownloadUrl(archivo)} target="_blank" rel="noopener noreferrer">Descargar</Button></div>
      </> : <Alert type="info" showIcon message="El archivo ya no está disponible." />}
    </Modal>
  </>;
}

export function AdjuntosPeriodo({ lavanderiaId, periodo }: { lavanderiaId: number; periodo: string }) {
  const { grupos, cargar } = useAdjuntosLavanderia(lavanderiaId, { periodo });
  if (!grupos.length) return null;
  return <Card size="small" title="Comprobantes recibidos por WhatsApp" style={{ marginBottom: 12 }}>
    {grupos.map((g) => <div key={g.operacionId} style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 8 }}>
      <AdjuntosLavanderia grupos={[g]} actualizar={cargar} />
      <Typography.Text>{g.movimiento.fecha} · {g.movimiento.descripcion} · {new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP" }).format(g.movimiento.monto)}</Typography.Text>
    </div>)}
  </Card>;
}
